/**
 * defaultSchedule.js — Single source of truth for the visit-type weekly
 * schedule used by both `usePatientForm.js` and `useProfile.js`.
 *
 * Why this file exists: the form-init scheduler needs to read the schedule
 * BEFORE the user has ever opened Profile Settings and saved it. Without a
 * shared default, the form silently fell through to `in_person_exam` on
 * fresh sessions even though Profile Settings would later show the correct
 * schedule. This caused the "Thursday PM should be virtual but defaults to
 * in-person" bug reported May 5, 2026.
 *
 * Both hooks now import from here, AND the form-init reader seeds
 * localStorage with this default if missing — so the next render is correct
 * and Profile Settings opens with the same data the form is using.
 */

export const DEFAULT_VISIT_SCHEDULE = {
  enabled: true,
  days: {
    monday:    { am: 'in_person_exam', pm: 'virtual' },
    tuesday:   { am: 'in_person_exam', pm: 'virtual' },
    wednesday: { am: 'in_person_exam', pm: 'virtual' },
    thursday:  { am: 'virtual',        pm: 'virtual' },
    friday:    { am: 'in_person_exam', pm: 'virtual' },
  },
};

/**
 * resolveScheduledVisitType — read the user's saved schedule (or seed default
 * if missing) and return the visit type for the current weekday + period.
 * Lazy-seeds localStorage with DEFAULT_VISIT_SCHEDULE if the key is missing
 * so subsequent reads see a consistent value.
 */
export function resolveScheduledVisitType(now = new Date()) {
  let schedule;
  try {
    const stored = localStorage.getItem('writeright_visit_schedule');
    if (stored) {
      schedule = JSON.parse(stored);
    } else {
      schedule = DEFAULT_VISIT_SCHEDULE;
      // Seed localStorage so future reads (and Profile Settings) see the
      // same data the form is using.
      try { localStorage.setItem('writeright_visit_schedule', JSON.stringify(DEFAULT_VISIT_SCHEDULE)); }
      catch { /* private mode / quota / fall through */ }
    }
  } catch {
    schedule = DEFAULT_VISIT_SCHEDULE;
  }
  if (!schedule || schedule.enabled === false || !schedule.days) {
    return 'in_person_exam';
  }
  const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const dayKey = dayNames[now.getDay()];
  const period = now.getHours() < 12 ? 'am' : 'pm';
  const daySchedule = schedule.days[dayKey];
  if (daySchedule && daySchedule[period]) return daySchedule[period];
  return 'in_person_exam';
}

export default DEFAULT_VISIT_SCHEDULE;
